import React, { useEffect, useState } from "react";
import { Box, FormControl, FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import { useForm } from "react-hook-form";
import moment from "moment";
import Colors from "assets/Style/Colors";
import SimpleDialog from "components/Dialog/SimpleDialog";
import DatePicker from "components/DatePicker";
import InputField from "components/Input";
import UploadFileSingle from "components/UploadFileSingle";
import { PrimaryButton } from "components/Buttons";
import { showErrorToast, showPromiseToast } from "components/NewToaster";
import { ErrorToaster } from "components/Toaster";
import VisaServices from "services/Visa";
import instance from "config/axios";
import routes from "services/System/routes";
import { CleanTypes } from "utils";

const allowFilesType = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "application/pdf",
  "application/vnd.ms-excel",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

// Edit a pending Cancellation / Absconder request: every input given when the request was created
// (request date, document and, for cancellation, Inside / Outside). Candidate name stays locked.
function EditProcessingRequestDialog({ open, onClose, item, onSaved }) {
  const { register } = useForm();
  const isCancel = String(item?.type || "").toLowerCase() === "cancel";

  const [requestDate, setRequestDate] = useState(null);
  const [additionalType, setAdditionalType] = useState("inside");
  const [document, setDocument] = useState("");

  useEffect(() => {
    if (open && item) {
      const firstStep = [...(item?.statuses || [])].sort((a, b) => a.id - b.id)[0];
      const date = firstStep?.date || item?.created_at;
      setRequestDate(date ? new Date(date) : null);
      setAdditionalType(String(item?.additional_type || "").toLowerCase().includes("out") ? "outside" : "inside");
      setDocument(item?.document || "");
    }
  }, [open, item]);

  const handleUploadDocument = async (e) => {
    const inputElement = e.target;
    e.preventDefault();
    try {
      const file = inputElement.files[0];
      if (!file) return null;
      if (!allowFilesType.includes(file.type)) {
        ErrorToaster(`Only ${CleanTypes(allowFilesType)} formats is supported`);
        return null;
      }
      if (file.size > 10 * 1024 * 1024) {
        showErrorToast("File Size Must Be Less than 10 MB");
        return null;
      }
      const currentDate = new Date().toISOString().split("T")[0];
      const formData = new FormData();
      formData.append("document", new File([file], `${currentDate}_${file.name}`, { type: file.type }));
      const { data } = await instance.post(routes.uploadDocuments, formData);
      return data?.data?.path;
    } catch (error) {
      ErrorToaster(error);
      return null;
    } finally {
      inputElement.value = "";
    }
  };

  const save = async () => {
    if (!requestDate || !moment(requestDate).isValid()) {
      showErrorToast("Please select request date");
      return;
    }
    if (!document) {
      showErrorToast("Please upload document");
      return;
    }
    try {
      const obj = {
        id: item?.id,
        date: moment(requestDate).format("MM-DD-YYYY"),
        document: document,
        ...(isCancel && { additional_type: additionalType }),
      };
      const promise = VisaServices.EditProcessingRequest(obj);
      showPromiseToast(promise, "Saving...", "Updated Successfully", "Something Went Wrong");
      const response = await promise;
      if (response?.responseCode === 200) {
        onClose();
        onSaved && onSaved();
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <SimpleDialog open={open} onClose={onClose} title={isCancel ? "Edit Cancellation Request" : "Edit Absconder Request"}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <InputField label={"Candidate Name :"} size={"small"} disabled={true} value={item?.candidate?.name || ""} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputField label={"Passport Number :"} size={"small"} disabled={true} value={item?.candidate?.passport_number || ""} />
        </Grid>
        <Grid item xs={12}>
          <DatePicker
            label={"Request Date :*"}
            value={requestDate}
            disableFuture={true}
            size={"small"}
            error={!requestDate ? "Please select request date." : ""}
            onChange={(date) => setRequestDate(date)}
          />
        </Grid>
        {isCancel && (
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <Box sx={{ fontWeight: "bold" }}>Type</Box>
              <RadioGroup row value={additionalType} onChange={(e) => setAdditionalType(e.target.value)}>
                <FormControlLabel value="inside" control={<Radio />} label="Inside" />
                <FormControlLabel value="outside" control={<Radio />} label="Outside" />
              </RadioGroup>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box sx={{ fontWeight: "bold", mb: 1 }}>Document : *</Box>
          <UploadFileSingle
            Memo={true}
            accept={allowFilesType}
            file={document}
            register={register("document", {
              onChange: async (e) => {
                const path = await handleUploadDocument(e);
                if (path) setDocument(path);
              },
            })}
          />
        </Grid>
        <Grid container sx={{ justifyContent: "center" }}>
          <Grid item xs={6} sm={6} sx={{ mt: 2, display: "flex", justifyContent: "space-between", gap: "25px" }}>
            <PrimaryButton bgcolor={Colors.primary} title="Update" disabled={!document || !requestDate} onClick={save} />
            <PrimaryButton onClick={onClose} bgcolor={"#FF1F25"} title="Cancel" />
          </Grid>
        </Grid>
      </Grid>
    </SimpleDialog>
  );
}

export default EditProcessingRequestDialog;
